import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { MultiSelect } from "@/components/ui/multi-select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChipOpcion, Icon } from "@/components/ds";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { formatNombreArea, ordenarAreas } from "@/lib/area";
import type { AreaSummary } from "@/types/area";
import type { Vehiculo } from "@/types/vehiculo";

/**
 * Tipos que ofrece el prototipo como atajo. NO es un catálogo cerrado: la
 * columna `tipo` es texto libre en la API y hay flotas cargadas con valores
 * fuera de esta lista, así que el valor actual del vehículo se agrega como un
 * chip más si no está acá. Elegir de una lista corta cubre el 99% de los casos
 * sin convertir el campo en un enum que rechace lo que ya existe.
 */
const TIPOS_SUGERIDOS = ["Camioneta", "Camión", "Van", "Bus", "Furgón", "Automóvil"];

/** Sugerencias del `datalist` de marca — se puede escribir cualquier otra. */
const MARCAS_SUGERIDAS = [
  "Toyota",
  "Nissan",
  "Ford",
  "Chevrolet",
  "Mitsubishi",
  "Volvo",
  "Scania",
  "Mercedes-Benz",
  "Hyundai",
];

export interface VehiculoFormProps {
  mode: "create" | "edit";
  vehiculo?: Vehiculo;
  /** Se invoca al terminar de guardar con el vehículo creado/actualizado. */
  onSaved: (vehiculo: Vehiculo) => void;
  /**
   * Render prop para las acciones del formulario (botón Guardar). Permite que
   * cada consumidor (diálogo de alta rápida vs. tab Información de la Ficha)
   * decida su propio contenedor/estilo, manteniendo el submit dentro del
   * `<form>`. Recibe también `resumen`, el texto que el pie del drawer usa
   * como pista de lo que falta. Mismo patrón que `TrabajadorForm`.
   */
  renderFooter: (state: { isSubmitting: boolean; resumen: string }) => ReactNode;
  /** Override del className del `<form>` — por defecto, layout simple en columna. */
  formClassName?: string;
  /**
   * Autofoco en el primer campo (Patente). Por defecto `true` — comportamiento
   * del diálogo de alta rápida. La Ficha a página completa lo desactiva para
   * no robar el foco (ni forzar scroll) al montar el tab Información.
   */
  autoFocusFirst?: boolean;
  /**
   * Cómo se ordenan los campos. Los campos, su validación y el payload se
   * declaran UNA sola vez; sólo cambia la disposición, para no terminar mante-
   * niendo las mismas reglas en dos componentes. Mismo prop que `TrabajadorForm`.
   *
   * - `dialogo` (por defecto): el drawer de alta, en bloques separados por
   *   divisores.
   * - `secciones`: tarjetas independientes para la Ficha.
   *
   * `activo` no pertenece a ninguno de los dos: lo maneja la Ficha con
   * `PATCH /vehiculos/{id}/estado`.
   */
  layout?: "dialogo" | "secciones";
}

/**
 * Cuerpo de campos del formulario de vehículo. Lo comparten el drawer de
 * "Nuevo vehículo" y el tab Información de la Ficha; la validación y el
 * contrato con la API son los mismos en los dos.
 */
export function VehiculoForm({
  mode,
  vehiculo,
  onSaved,
  renderFooter,
  formClassName,
  autoFocusFirst = true,
  layout = "dialogo",
}: VehiculoFormProps) {
  const uid = useId();
  const [patente, setPatente] = useState(vehiculo?.patente ?? "");
  const [sigla, setSigla] = useState(vehiculo?.sigla ?? "");
  const [tipo, setTipo] = useState(vehiculo?.tipo ?? "");
  const [marca, setMarca] = useState(vehiculo?.marca ?? "");
  const [modelo, setModelo] = useState(vehiculo?.modelo ?? "");
  const [anio, setAnio] = useState<string>(vehiculo?.anio != null ? String(vehiculo.anio) : "");
  const [areas, setAreas] = useState<AreaSummary[]>([]);
  const [areasIds, setAreasIds] = useState<string[]>(
    vehiculo?.areas?.map((a) => String(a.id)) ?? [],
  );
  const [isLoadingAreas, setIsLoadingAreas] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const esSecciones = layout === "secciones";

  const areasOrdenadas = useMemo(() => ordenarAreas(areas), [areas]);
  const areasOptions = useMemo(
    () => areasOrdenadas.map((a) => ({ value: String(a.id), label: formatNombreArea(a) })),
    [areasOrdenadas],
  );

  /** Los 6 sugeridos más el valor actual, si el vehículo trae uno fuera de la lista. */
  const tiposChips = useMemo(() => {
    const actual = tipo.trim();
    if (actual && !TIPOS_SUGERIDOS.includes(actual)) return [...TIPOS_SUGERIDOS, actual];
    return TIPOS_SUGERIDOS;
  }, [tipo]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ data: AreaSummary[] }>("/areas?all=1");
        if (!cancelled) setAreas(res.data);
      } catch {
        toast.error("No se pudieron cargar las áreas.");
      } finally {
        if (!cancelled) setIsLoadingAreas(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Qué falta para poder guardar. El pie del drawer lo muestra como pista en
   * vez de deshabilitar el botón en silencio: el usuario ve qué le falta antes
   * de intentar. Mismo criterio que el prototipo.
   */
  const resumen = useMemo(() => {
    const falta = [
      !patente.trim() && "la patente",
      !tipo.trim() && "el tipo",
      !marca.trim() && "la marca",
      !modelo.trim() && "el modelo",
    ].filter(Boolean) as string[];

    if (falta.length === 0) {
      return areasIds.length > 0
        ? "Queda disponible para los checklists de las áreas elegidas."
        : "Sin área queda visible para toda la empresa al hacer un checklist.";
    }
    const lista =
      falta.length > 1 ? `${falta.slice(0, -1).join(", ")} y ${falta[falta.length - 1]}` : falta[0];
    return `Falta ${lista}.`;
  }, [patente, tipo, marca, modelo, areasIds]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!patente.trim()) {
      toast.error("La patente es requerida.");
      return;
    }
    if (!tipo.trim()) {
      toast.error("El tipo es requerido.");
      return;
    }
    if (!marca.trim()) {
      toast.error("La marca es requerida.");
      return;
    }
    if (!modelo.trim()) {
      toast.error("El modelo es requerido.");
      return;
    }
    const anioNum = anio.trim() ? Number(anio) : null;
    if (anio.trim() && (!Number.isInteger(anioNum) || anioNum! < 1900 || anioNum! > 2100)) {
      toast.error("El año debe estar entre 1900 y 2100.");
      return;
    }

    const payload = {
      patente: patente.trim().toUpperCase(),
      sigla: sigla.trim() || null,
      tipo: tipo.trim(),
      marca: marca.trim(),
      modelo: modelo.trim(),
      anio: anioNum,
      // `activo` NO viaja nunca desde el formulario. En la Ficha lo maneja el
      // interruptor inmediato (`PATCH /vehiculos/{id}/estado`) y mandarlo acá
      // revertiría un activar/desactivar recién hecho. En el alta un vehículo
      // nace en operación y punto: la API lo da por sentado con `sometimes` en
      // la regla, `default(true)` en la columna y `$attributes` en el modelo.
      areas_ids: areasIds.map(Number),
    };

    setIsSubmitting(true);
    try {
      if (mode === "create") {
        const res = await api.post<{ data: Vehiculo }>("/vehiculos", payload);
        toast.success("Vehículo creado.");
        onSaved(res.data);
      } else if (vehiculo) {
        const res = await api.put<{ data: Vehiculo }>(`/vehiculos/${vehiculo.id}`, payload);
        toast.success("Vehículo actualizado.");
        onSaved(res.data);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.errors) {
        const firstError = Object.values(err.errors)[0]?.[0];
        toast.error(firstError ?? "Revisa los datos.");
      } else {
        toast.error("No se pudo guardar el vehículo.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Campos ────────────────────────────────────────────────────────────────
  // Se declaran una vez y cada layout los ordena.

  const campoPatente = (
    <div className="space-y-1.5">
      <Label htmlFor={`${uid}-patente`}>
        Patente <span className="text-destructive">*</span>
      </Label>
      <Input
        id={`${uid}-patente`}
        value={patente}
        onChange={(e) => setPatente(e.target.value.toUpperCase())}
        placeholder="JTRX-12"
        disabled={isSubmitting}
        autoFocus={autoFocusFirst}
        required
        className="fc-num tracking-[.06em]"
      />
    </div>
  );

  const campoSigla = (
    <div className="space-y-1.5">
      <Label htmlFor={`${uid}-sigla`}>Sigla interna</Label>
      <Input
        id={`${uid}-sigla`}
        value={sigla}
        onChange={(e) => setSigla(e.target.value.toUpperCase())}
        placeholder="V-01"
        disabled={isSubmitting}
        className="fc-num"
      />
    </div>
  );

  const campoTipoChips = (
    <div className="space-y-1.5">
      <Label>
        Tipo de vehículo <span className="text-destructive">*</span>
      </Label>
      <div className="flex flex-wrap gap-1.5">
        {tiposChips.map((t) => (
          <ChipOpcion
            key={t}
            activo={tipo === t}
            disabled={isSubmitting}
            // Volver a tocar el chip activo lo deselecciona: sin esto no hay
            // forma de dejar el campo vacío una vez elegido.
            onClick={() => setTipo(tipo === t ? "" : t)}
          >
            {t}
          </ChipOpcion>
        ))}
      </div>
    </div>
  );

  const campoTipoTexto = (
    <div className="space-y-1.5">
      <Label htmlFor={`${uid}-tipo`}>
        Tipo <span className="text-destructive">*</span>
      </Label>
      <Input
        id={`${uid}-tipo`}
        value={tipo}
        onChange={(e) => setTipo(e.target.value)}
        placeholder="Camioneta, Camión, Van…"
        disabled={isSubmitting}
        required
        list={`${uid}-tipos`}
      />
      <datalist id={`${uid}-tipos`}>
        {TIPOS_SUGERIDOS.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
    </div>
  );

  const campoMarca = (
    <div className="space-y-1.5">
      <Label htmlFor={`${uid}-marca`}>
        Marca <span className="text-destructive">*</span>
      </Label>
      <Input
        id={`${uid}-marca`}
        value={marca}
        onChange={(e) => setMarca(e.target.value)}
        placeholder="Toyota"
        disabled={isSubmitting}
        required
        list={`${uid}-marcas`}
      />
      <datalist id={`${uid}-marcas`}>
        {MARCAS_SUGERIDAS.map((m) => (
          <option key={m} value={m} />
        ))}
      </datalist>
    </div>
  );

  const campoModelo = (
    <div className="space-y-1.5">
      <Label htmlFor={`${uid}-modelo`}>
        Modelo <span className="text-destructive">*</span>
      </Label>
      <Input
        id={`${uid}-modelo`}
        value={modelo}
        onChange={(e) => setModelo(e.target.value)}
        placeholder="Hilux"
        disabled={isSubmitting}
        required
      />
    </div>
  );

  const campoAnio = (
    <div className="space-y-1.5">
      <Label htmlFor={`${uid}-anio`}>Año</Label>
      <Input
        id={`${uid}-anio`}
        inputMode="numeric"
        value={anio}
        onChange={(e) => setAnio(e.target.value.replace(/\D/g, "").slice(0, 4))}
        placeholder="2026"
        disabled={isSubmitting}
        className="fc-num w-[92px] shrink-0"
      />
    </div>
  );

  const campoAreas = (
    <div className="space-y-1.5">
      <Label>Áreas donde opera</Label>
      <MultiSelect
        options={areasOptions}
        value={areasIds}
        onChange={setAreasIds}
        placeholder={
          isLoadingAreas
            ? "Cargando áreas…"
            : areasOptions.length === 0
              ? "No hay áreas creadas todavía"
              : "Selecciona áreas"
        }
        emptyText="Sin áreas que coincidan."
        disabled={isSubmitting || isLoadingAreas || areasOptions.length === 0}
      />
      {/* Las áreas son un clasificador OPCIONAL: el texto explica qué pasa si
          se dejan vacías, en vez de sugerir que son obligatorias. */}
      <p className="text-soft text-[11.5px] leading-[1.5]">
        {areasIds.length > 0
          ? "Sólo quienes trabajan en esas áreas lo verán al hacer un checklist."
          : "Sin área queda visible para toda la empresa al hacer un checklist."}
      </p>
    </div>
  );

  // ── Layouts ───────────────────────────────────────────────────────────────

  if (esSecciones) {
    return (
      <form onSubmit={submit} className={formClassName ?? "space-y-3"}>
        <section className="bg-card flex flex-col gap-3.5 rounded-xl border px-4 py-4">
          <span className="fc-section-title">Identificación</span>
          {/* auto-fit para que envuelva en vez de comprimirse (regla de
              grillas del DS), igual que el prototipo. */}
          <div
            className="grid gap-x-4 gap-y-3"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
          >
            {campoPatente}
            {campoSigla}
            {campoTipoTexto}
            {campoMarca}
            {campoModelo}
            {campoAnio}
            {campoAreas}
          </div>

          {/* Guardar va DENTRO de la tarjeta y no suelto al final: ahí se ve
              sin buscarlo y se entiende qué guarda — lo que está justo arriba.
              Es el lugar del prototipo y la misma convención que
              `TrabajadorForm`, que lo pone dentro de su última sección.

              Fue el motivo original de sacar `activo` de acá: un interruptor
              POR DEBAJO del botón que lo guarda promete algo que no cumple.
              Hoy `activo` tampoco está en el alta — vive sólo en la Ficha. */}
          {renderFooter({ isSubmitting, resumen })}
        </section>
      </form>
    );
  }

  return (
    <form onSubmit={submit} className={formClassName ?? "space-y-0"}>
      <div className="border-border-subtle flex flex-col gap-3.5 border-b px-1 pb-4">
        <div className="grid gap-3 sm:grid-cols-[1.1fr_1fr]" data-tour="vehiculos-patente">
          {campoPatente}
          {campoSigla}
        </div>
        <div data-tour="vehiculos-tipo-marca-modelo" className="space-y-3.5">
          {campoTipoChips}
          <div className="grid gap-3 sm:grid-cols-2">
            {campoMarca}
            {campoModelo}
          </div>
        </div>
        {campoAnio}
      </div>

      {/* Sin `border-b`: es el último bloque del cuerpo y el pie del drawer ya
          trae su propio `border-t`. Con los dos quedaba un divisor huérfano. */}
      <div className="px-1 py-4">{campoAreas}</div>

      {renderFooter({ isSubmitting, resumen })}
    </form>
  );
}

/** Ícono del encabezado del drawer — el mismo glifo que el estado vacío del listado. */
export function VehiculoDrawerIcon() {
  return (
    <span className="bg-primary-50 text-primary-hover flex size-9 shrink-0 items-center justify-center rounded-[10px]">
      <Icon name="local_shipping" size={20} />
    </span>
  );
}
